import Icon from "@/components/Icon";
import Tooltip from "@/components/Tooltip";
import { EventRegistrationAdditionalStatus, EventRegistrationStatus, RegistrantRow } from "../types";
import Tag, { TagType } from "@/components/Tag/Tag";
import { toDateFormat } from "@/lib/dateHelpers";
import { DateTime } from "luxon";
import { isExcessiveScore } from "../_utils";

interface RegistrantStatusCellProps {
  row: Pick<RegistrantRow, "status" | "additionalStatus" | "note" | "score_total" | "date" | "suspension" | "attended">;
}

const SUSPENSION_DAYS = 90;

type StatusTag = { color: TagType; label: string };

const STATUS_MAP: Record<EventRegistrationStatus, StatusTag> = {
  [EventRegistrationStatus.FAILED]: { color: "error", label: "Fail" },
  [EventRegistrationStatus.PASSED]: { color: "success", label: "Pass" },
  [EventRegistrationStatus.QUALIFIED]: { color: "warn", label: "Qualifying" }
};

const ADDITIONAL_STATUS_MAP: Record<EventRegistrationAdditionalStatus, string> = {
  [EventRegistrationAdditionalStatus.CHEATING]: "Cheating",
  [EventRegistrationAdditionalStatus.EXCUSED]: "Excused",
  [EventRegistrationAdditionalStatus.NO_CARD]: "No Card",
  [EventRegistrationAdditionalStatus.NO_SHOW]: "No Show",
  [EventRegistrationAdditionalStatus.POOR_CONDUCT]: "Poor Conduct",
  [EventRegistrationAdditionalStatus.WEATHER_EVENT]: "Weather Event",
  [EventRegistrationAdditionalStatus.WITHDRAWAL]: "Withdrawal",
  [EventRegistrationAdditionalStatus.MEDICAL_WITHDRAWAL]: "Medical Withdrawal",
  [EventRegistrationAdditionalStatus.DISQUALIFIED]: "Disqualified"
};

const ResultStatusCell = (status: EventRegistrationStatus, isExcused: boolean, note: string) => {
  const data = STATUS_MAP[status as EventRegistrationStatus];

  const tag = (
    <Tag tagType={data?.color as TagType} testId={data.label}>
      {!!note && <Icon name="error" size={20} className="mr-1" />}
      {data.label}
    </Tag>
  );

  if (isExcused) {
    return (
      <Tooltip
        variant="plain"
        color="black"
        supportingText={["Excused", note].join(",\n")}
        menuCorner="end-start"
        anchorCorner="start-start"
        positioning="fixed"
        anchorClassName="md:-mx-0 md:px-0"
      >
        {tag}
      </Tooltip>
    );
  }

  return tag;
};

const SuspensionStatusCell = (
  additionalStatus: EventRegistrationAdditionalStatus,
  note: string,
  date: string,
  suspension?: RegistrantRow["suspension"]
) => {
  const startDate = toDateFormat(date);
  const hasSuspensionData = !!suspension?.startDate;
  const endDate = hasSuspensionData
    ? !!suspension.endDate
      ? toDateFormat(suspension.endDate)
      : "Indefinite"
    : toDateFormat(DateTime.fromISO(date).plus({ days: SUSPENSION_DAYS }).toISO() || "");

  const suspensionDates = `PAT Suspension ${startDate} - ${endDate}`;
  const tooltipText = note ? suspensionDates + ", \n" + note : suspensionDates;
  const statusText = ADDITIONAL_STATUS_MAP[additionalStatus];

  return (
    <Tooltip
      variant="plain"
      color="black"
      supportingText={tooltipText}
      menuCorner="end-start"
      anchorCorner="start-start"
      positioning="fixed"
      anchorClassName="md:-mx-0 md:px-0"
    >
      <div className="flex flex-row items-center">
        {tooltipText && <Icon name="error" className="text-sys-brand-error mr-1" />}
        {statusText}
      </div>
    </Tooltip>
  );
};

const ExcusedStatusCell = (note: string) => (
  <Tooltip
    variant="plain"
    color="black"
    supportingText={note}
    menuCorner="end-start"
    anchorCorner="start-start"
    positioning="fixed"
    anchorClassName="md:-mx-0 md:px-0"
  >
    <Tag tagType="information">
      <Icon name="error" size={20} className="mr-1" />
      Excused
    </Tag>
  </Tooltip>
);

const ExcessiveScoreStatusCell = (note: string) => (
  <Tooltip
    variant="plain"
    color="black"
    supportingText={note || "PAT Suspension - Indefinite"}
    menuCorner="end-start"
    anchorCorner="start-start"
    positioning="fixed"
    anchorClassName="md:-mx-0 md:px-0"
  >
    <div className="flex flex-row items-center">
      <Icon name="error" className="text-sys-brand-error mr-1" />
      Excessive Score
    </div>
  </Tooltip>
);

function isAdditionalStatus(additionalStatus: string): additionalStatus is EventRegistrationAdditionalStatus {
  return Object.values(EventRegistrationAdditionalStatus).includes(
    additionalStatus as EventRegistrationAdditionalStatus
  );
}

function isStatus(additionalStatus: string): additionalStatus is EventRegistrationStatus {
  return Object.values(EventRegistrationStatus).includes(additionalStatus as EventRegistrationStatus);
}

const WeatherConditionsStatusCell = (note?: string) => {
  return (
    <Tooltip
      variant="plain"
      color="black"
      supportingText={note || "Weather event prevented completion of the PAT"}
      menuCorner="end-start"
      anchorCorner="start-start"
      positioning="fixed"
      anchorClassName="md:-mx-0 md:px-0"
    >
      <div className="flex flex-row items-center">
        <Icon name="error" className="text-sys-brand-error mr-1" />
        Weather Event
      </div>
    </Tooltip>
  );
};

const LateCancelStatusCell = (suspension?: RegistrantRow["suspension"]) => {
  const startDate = suspension?.startDate;
  const endDate = suspension?.endDate;

  return (
    <Tooltip
      variant="plain"
      color="black"
      supportingText={`PAT Suspension ${startDate} - ${endDate},\nLess than 72 hours notice`}
      menuCorner="end-start"
      anchorCorner="start-start"
      positioning="fixed"
      anchorClassName="md:-mx-0 md:px-0"
    >
      <div className="flex flex-row items-center">
        <Icon name="error" className="text-sys-brand-error mr-1" />
        Late Cancel
      </div>
    </Tooltip>
  );
};

export default function RegistrantStatusCell({ row }: RegistrantStatusCellProps) {
  const { status, additionalStatus, note, score_total, suspension } = row;

  const isExcused = !!additionalStatus && additionalStatus === EventRegistrationAdditionalStatus.EXCUSED;
  const isWeatherEvent = !!additionalStatus && additionalStatus === EventRegistrationAdditionalStatus.WEATHER_EVENT;
  const hasExcessiveScore = score_total && isExcessiveScore(score_total);

  if (additionalStatus && !isExcused && isAdditionalStatus(additionalStatus) && !isWeatherEvent)
    return SuspensionStatusCell(additionalStatus, note, row.date, suspension);
  if (isWeatherEvent) return WeatherConditionsStatusCell(note);
  if (isExcused) return ExcusedStatusCell(note);
  if (hasExcessiveScore) return ExcessiveScoreStatusCell(note);
  if (status && isStatus(status)) return ResultStatusCell(status, isExcused, note);
  if (additionalStatus === "late_cancel") return LateCancelStatusCell(suspension);
  return null;
}
